import { useEffect, useState } from 'preact/hooks'
import { FileText, Save, X, Globe, Calendar } from 'lucide-preact'
import { websApi, type WebViewDto } from '@/api/webs'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { TextBoxField } from '@/components'
import { RichTextBox } from '@/components/common/RichTextBox'
import { Controller } from 'react-hook-form'
import { MarkdownView } from '@/components/common/MarkdownView'
import { DetailRow } from './DetailRow'
import { useAIForm, handleListFieldUpdate } from '@/hooks/useAIForm'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useBenefitsList } from '@/hooks/useBenefitsList'
import { BenefitsCard } from './BenefitsCard'
import { EditButton } from './EditButton'
import { SignedOffNote } from './SignedOffNote'

export function DemoPanel({ web, onPatchWeb, isSignedOff, routeToTab }: { web: WebViewDto; onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void; isSignedOff?: boolean; routeToTab?: (tab: WebTabKey) => void }) {
  const demo = web.Demo
  const { showSuccess, showError, showErrorUnless422 } = useToast()
  const { canEdit } = useWorkspaceCapabilities()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingBenefits, setEditingBenefits] = useState(false)

  type DemoForm = { title: string; overview: string; calendlyUrl: string; benefits: { value: string }[] }

  const { form } = useAIForm<DemoForm>({
    mode: 'onChange',
    defaultValues: { title: demo?.Title || '', overview: demo?.Overview || '', calendlyUrl: demo?.CalendlyUrl || '', benefits: (demo?.Benefits || []).map(b => ({ value: b })) }
  }, {
    aiContext: { campaignId: web.CampaignId, webId: web.Id, location: ChatLocations.WebDemoEditor, entityType: ChatEntityTypes.Web, entityId: web.Id, viewMode: editing || editingBenefits ? ChatViewModes.Edit : ChatViewModes.View },
    fields: [
      { aiName: 'demoTitle', formPath: 'title', displayName: 'Demo Title', fieldType: 'text', maxLength: 200, isEditable: editing },
      { aiName: 'demoOverview', formPath: 'overview', displayName: 'Demo Overview', fieldType: 'textarea', maxLength: 5000, isEditable: editing },
      { aiName: 'demoCalendlyUrl', formPath: 'calendlyUrl', displayName: 'Calendly URL', fieldType: 'text', maxLength: 500, isEditable: editing },
      { aiName: 'demoBenefits', formPath: 'benefits', displayName: 'Why Book a Demo Benefits', fieldType: 'textarea', isEditable: editingBenefits, isListField: true },
    ],
    onFieldUpdate: (aiName, value) => {
      if (aiName === 'demoBenefits') handleListFieldUpdate(value, benefits.append)
    },
  })

  const benefits = useBenefitsList({
    form,
    serverBenefits: demo?.Benefits || [],
    editing: editingBenefits,
    setEditing: setEditingBenefits,
    onSave: async (clean) => {
      try {
        const res = await websApi.updateDemoBenefits(web.WorkspaceId, web.Id, { ETag: web.ETag, Benefits: clean })
        if (!res.error && res.data) { onPatchWeb(prev => ({ ...prev, ...res.data })); showSuccess('Updated', 'Why Book a Demo list saved.'); return true }
        showErrorUnless422('Save failed', res, 'Unable to update Why Book a Demo list')
        return false
      } catch { showError('Save failed', 'Network or server error'); return false }
    },
  })

  const { watch, reset, control, formState } = form

  useEffect(() => { reset({ title: demo?.Title || '', overview: demo?.Overview || '', calendlyUrl: demo?.CalendlyUrl || '', benefits: (demo?.Benefits || []).map(b => ({ value: b })) }) }, [demo?.Title, demo?.Overview, demo?.CalendlyUrl, demo?.Benefits])

  const w = watch()
  const initial = { title: demo?.Title || '', overview: demo?.Overview || '', calendlyUrl: demo?.CalendlyUrl || '' }
  const current = { title: (w.title || '').trim(), overview: (w.overview || '').trim(), calendlyUrl: (w.calendlyUrl || '').trim() }
  const isChanged = JSON.stringify(initial) !== JSON.stringify(current)
  const calendlyUrlError = formState.errors.calendlyUrl?.message as string | undefined
  const save = async () => { setSaving(true); try { const dto = { ETag: web.ETag, Title: current.title || undefined, Overview: current.overview ?? '', CalendlyUrl: current.calendlyUrl !== initial.calendlyUrl ? current.calendlyUrl : undefined }; const res = await websApi.updateDemoDetails(web.WorkspaceId, web.Id, dto); if (!res.error && res.data) { onPatchWeb(prev => ({ ...prev, ...res.data })); showSuccess('Updated', 'Demo details saved.'); setEditing(false) } else showErrorUnless422('Save failed', res, 'Unable to update demo details') } catch { showError('Save failed', 'Network or server error') } setSaving(false) }
  const cancel = () => { setEditing(false); reset() }

  return (
    <>
    {isSignedOff && <SignedOffNote locked="demo details and the Why Book a Demo list" />}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left column: Demo details */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><FileText className="w-5 h-5 text-primary-600" /> Demo details</h2>
          {!editing ? (
            canEdit && <EditButton onClick={() => setEditing(true)} disabled={isSignedOff} />
          ) : (
            <div className="flex items-center gap-2">
              <button type="button" disabled={saving || !isChanged || (w.title.trim().length === 0) || !!calendlyUrlError} onClick={save} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
              <button type="button" onClick={cancel} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
            </div>
          )}
        </div>
        {!editing ? (
          <div className="mt-3 space-y-3 text-[13px]">
            <DetailRow label="Title" value={demo?.Title || '—'} />
            <DetailRow label="Overview" value={demo?.Overview ? <MarkdownView>{demo.Overview}</MarkdownView> : '—'} />
            <DetailRow label="Calendly URL" value={demo?.CalendlyUrl ? (<a href={demo.CalendlyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:underline"><Calendar className="w-4 h-4" /> {demo.CalendlyUrl}</a>) : '—'} />
          </div>
        ) : (
          <form className="mt-3 space-y-3" onSubmit={e => { e.preventDefault() }}>
            <TextBoxField control={control} name="title" labelSize="xs" label="Title" showCharCounter maxLength={200} rules={{ validate: v => (v ?? '').trim().length > 0 || 'Required' }} placeholder="Title" />
            <Controller name="overview" control={control} render={({ field }) => (<RichTextBox name="overview" label="Overview" labelSize="xs" value={field.value} onChange={field.onChange} onBlur={field.onBlur} placeholder="Optional overview" minHeight="120px" />)} />
            <TextBoxField control={control} name="calendlyUrl" labelSize="xs" label="Calendly URL" type="url" showCharCounter maxLength={500} rules={{ maxLength: { value: 500, message: 'Calendly URL exceeds maximum length of 500 characters.' }, validate: v => !v || /^https:\/\/.+/.test(v) || 'Calendly URL must be a valid https:// URL.' }} placeholder="e.g. https://calendly.com/your-link" />
          </form>
        )}
      </div>
      {/* Right column: Why Book a Demo */}
      <BenefitsCard title="Why Book a Demo" canEdit={canEdit} editDisabled={isSignedOff} {...benefits} />
    </div>
    {isSignedOff && routeToTab && (
      <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Landing Page</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Generate a landing page for this demo to drive requests.
            </p>
          </div>
          <button
            type="button"
            onClick={() => routeToTab('media')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Globe className="w-4 h-4" />
            Generate Landing Page
          </button>
        </div>
      </div>
    )}
    </>
  )
}
